import React, { useState } from 'react';
import { Users } from 'lucide-react';
import RoleSelection from './RoleSelection';
import StudentInfoForm from './StudentInfoForm';
import VeterinarianInfoForm from './VeterinarianInfoForm';
import EmailVerification from './EmailVerification';
import CreatePassword from './CreatePassword';

const STEPS = ['Role', 'Info', 'Verify', 'Password'];

const StepIndicator = ({ current, total }) => (
  <div className="flex items-center gap-1.5 mb-8 md:hidden">
    {Array.from({ length: total }, (_, i) => (
      <React.Fragment key={i}>
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${
            i < current ? 'bg-indigo-600' : i === current ? 'bg-indigo-400' : 'bg-gray-200'
          } ${i === current ? 'flex-[2]' : 'flex-1'}`}
        />
      </React.Fragment>
    ))}
  </div>
);

const RegisterPage = () => {
  const [step, setStep] = useState(0);
  const [role, setRole] = useState('');
  const [userData, setUserData] = useState(null);
  const [registrationState, setRegistrationState] = useState(null);

  const handleRoleSelect = (selectedRole, advance) => {
    setRole(selectedRole);
    if (advance) setStep(1);
  };

  const handleUserInfoSubmit = (data) => {
    setUserData({ ...data, role });
    setRegistrationState(null);
    setStep(2);
  };

  const handleVerified = () => {
    setStep(3);
  };

  return (
    <div className="flex min-h-screen w-full bg-gray-50">
      {/* Left Side — Branding */}
      <div className="hidden md:flex flex-col justify-between md:w-[35%] lg:w-[45%] bg-[#616ff6] p-10 lg:p-16 text-white relative overflow-hidden">
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <Users size={36} />
            <div>
              <h1 className="text-3xl font-bold tracking-tight leading-none mb-1">Vet Collab</h1>
              <p className="text-xs text-indigo-100 font-medium">powered by furcare</p>
            </div>
          </div>

          <div className="max-w-sm mt-16">
            <p className="text-base leading-relaxed text-indigo-50">
              Join thousands of veterinary professionals and students sharing knowledge, cases, and advancing animal care together.
            </p>
          </div>
        </div>

        {/* Vertical Step Indicator */}
        <div className="relative z-10 mt-auto mb-8">
          <div className="space-y-5">
            {[
              { num: 1, label: 'Choose your role' },
              { num: 2, label: 'Your information' },
              { num: 3, label: 'Create password' },
            ].map(({ num, label }) => {
              let isActive = false;
              if (num === 1 && step === 0) isActive = true;
              if (num === 2 && (step === 1 || step === 2)) isActive = true;
              if (num === 3 && step === 3) isActive = true;

              return (
                <div key={num} className="flex items-center gap-4">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition-colors ${isActive ? 'bg-white text-[#616ff6]' : 'bg-white/20 text-white'}`}>
                    {num}
                  </div>
                  <span className={`text-sm transition-colors ${isActive ? 'text-white font-medium' : 'text-indigo-100/70 font-light'}`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Side — Wizard */}
      <div className="flex-1 flex items-center justify-center py-2 px-6 lg:py-4">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden flex items-center space-x-2 text-indigo-600 mb-8">
            <Users size={26} />
            <span className="font-bold text-2xl tracking-tight">Vet Collab</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl py-4 px-6 sm:py-5">
            <StepIndicator current={step} total={STEPS.length} />

            {step === 0 && (
              <RoleSelection selected={role} onSelect={handleRoleSelect} />
            )}

            {step === 1 && role === 'student' && (
              <StudentInfoForm
                initialData={userData}
                onNext={handleUserInfoSubmit}
                onBack={() => setStep(0)}
              />
            )}
            
            {step === 1 && role === 'veterinarian' && (
              <VeterinarianInfoForm
                initialData={userData}
                onNext={handleUserInfoSubmit}
                onBack={() => setStep(0)}
              />
            )}

            {step === 2 && (
              <EmailVerification
                studentData={userData}
                registrationState={registrationState}
                setRegistrationState={setRegistrationState}
                onNext={handleVerified}
                onBack={() => setStep(1)}
              />
            )}

            {step === 3 && registrationState && (
              <CreatePassword
                registrationId={registrationState.registrationId}
                studentData={userData}
                onBack={() => setStep(2)}
              />
            )}

            <div className="mt-8 pt-6 border-t border-gray-100">
              <p className="text-center text-sm text-gray-500">
                Already have an account?{' '}
                <a href="/login" className="text-[#616ff6] font-semibold hover:text-indigo-800 transition-colors">
                  Sign in
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
